import * as React from 'react'

type RemoveConfirmDialogProps = {
  title: string
  description: string
  onCancel: () => void
  onConfirm: () => void
}

// A modal confirmation for removing a draft item (a notification, a question…)
// from the cycle builder. Removal is a local (unsaved) draft edit, so there's no
// async "removing" state — the overlay blocks the wizard behind it and clicking
// it (or Esc) cancels. Title/description are supplied by the caller so the same
// dialog serves every "remove this X?" prompt in the wizard.
export const RemoveConfirmDialog = ({
  title,
  description,
  onCancel,
  onConfirm,
}: RemoveConfirmDialogProps) => {
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-confirm-title"
      onClick={onCancel}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(29,38,74,0.22)] px-4"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-[460px] rounded-[12px] bg-white p-7 shadow-[0_18px_44px_rgba(29,38,74,0.18)]"
      >
        <h3
          id="remove-confirm-title"
          className="font-talent text-[21px] font-medium leading-[26px] text-[#1D264A]"
        >
          {title}
        </h3>
        <p className="mt-2 text-[16px] leading-[22px] text-[#5E6580]">
          {description}
        </p>

        <div className="mt-7 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-[44px] items-center justify-center rounded-lg border border-[#D7DBE8] bg-white px-6 text-[16px] font-semibold text-[#1D264A] hover:bg-[#F7F8FB]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-[44px] items-center justify-center rounded-lg bg-[#E42309] px-6 text-[16px] font-semibold text-white hover:bg-[#cc1f08]"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}
